import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import api, {
  bindAuthHandlers,
  refreshSession,
  safeReturnTo,
  setAccessToken,
} from '../api/client';

const AuthContext = createContext(null);
const AUTH_CHANNEL = 'po-parser-auth';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const channelRef = useRef(null);

  const clearLocal = useCallback((broadcast) => {
    setAccessToken(null);
    setUser(null);
    if (broadcast && channelRef.current) {
      try {
        channelRef.current.postMessage({ type: 'logout' });
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    bindAuthHandlers({
      onUser: (next) => {
        if (!cancelled) setUser(next);
      },
      onLogout: () => {
        if (cancelled) return;
        clearLocal(true);
        if (!window.location.pathname.startsWith('/auth/')) {
          window.location.assign('/auth/login');
        }
      },
    });

    refreshSession()
      .catch(() => {
        setAccessToken(null);
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    let channel = null;
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL);
      channelRef.current = channel;
      channel.onmessage = (ev) => {
        if (ev.data?.type === 'logout') {
          setAccessToken(null);
          setUser(null);
          if (!window.location.pathname.startsWith('/auth/')) {
            window.location.assign('/auth/login');
          }
        }
      };
    } catch {
      channelRef.current = null;
    }

    return () => {
      cancelled = true;
      bindAuthHandlers({ onUser: null, onLogout: null });
      if (channel) channel.close();
    };
  }, [clearLocal]);

  const login = useCallback(async (returnTo) => {
    const { data } = await api.post(
      '/api/auth/login',
      { return_to: safeReturnTo(returnTo) },
      { skipAuthRefresh: true },
    );
    if (!data?.authorize_url) {
      throw new Error('Login did not return authorize_url');
    }
    window.location.assign(data.authorize_url);
  }, []);

  const completeLogin = useCallback((token, nextUser) => {
    setAccessToken(token);
    setUser(nextUser);
  }, []);

  const logout = useCallback(async () => {
    let endSessionUrl = '';
    try {
      const { data } = await api.post('/api/auth/logout', {}, { skipAuthRefresh: true });
      endSessionUrl = data?.end_session_url || '';
    } catch {
      /* still clear local session */
    }
    clearLocal(true);
    window.location.assign(endSessionUrl || '/auth/login');
  }, [clearLocal]);

  const value = useMemo(
    () => ({
      user,
      ready,
      isAdmin: (user?.role || '') === 'admin',
      login,
      logout,
      completeLogin,
    }),
    [user, ready, login, logout, completeLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}

export function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    const q = next.startsWith('/auth/') ? '' : `?return_to=${encodeURIComponent(next)}`;
    return <Navigate to={`/auth/login${q}`} replace />;
  }
  return children;
}

export function RequireAdmin({ children }) {
  const { user, isAdmin, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/auth/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}